import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { PackagingApiService } from 'src/utilities/services/packaging.api.service';
import { SelectListsApiService } from 'src/utilities/services/selectLists.api.service';
import { PackagingStatusHelper } from 'src/helpers/PackagingStatusHelper';
import { SelectList, SelectListParams } from 'src/utilities/selectLists/selectList';
import { FormControl, FormGroup } from '@angular/forms';
import { SessionService } from 'src/utilities/session/session.service';
import { LocationsTypes } from 'src/helpers/LocationsHelper';
import { PackagingStatusCount } from 'src/utilities/interfaces/PackagingStatusCount.interface';
import { FilterField } from '../shared/components/generic-filter/filter-field.interface';
import { PackagingDateTypes } from 'src/helpers/PackagingHelper';
import { SearchStateService } from 'src/utilities/services/search-state.service';
import { Modules, Permissions } from 'src/utilities/interfaces/permissions.api.interface';
import { PermissionsConfig } from 'src/utilities/permissions/permissions';

@Component({
  selector: 'app-index',
  templateUrl: './index.component.html',
  styleUrls: ['./index.component.scss']
})
export class IndexComponent implements OnInit {

  statusCounts: PackagingStatusCount[] = [];
  loading = false;
  PackagingStatusHelper = PackagingStatusHelper;
  hasRoleDashboardView: boolean = false;
  hasRolePackagingsList: boolean = false;

  // Filtros
  filterForm = new FormGroup({
    idCenter: new FormControl(null),
    idArea: new FormControl(null)
  });

  centersList: SelectList[] = [];
  areasList: SelectList[] = [];
  filterFields: FilterField[] = [];

  constructor(
    private packagingApi: PackagingApiService,
    private selectListsApiService: SelectListsApiService,
    private router: Router,
    public sessionService: SessionService,
    private searchState: SearchStateService
  ) { }

  /**
   * Obtener el nombre completo del usuario (nombre + apellido si lo tiene)
   */
  get userFullName(): string {
    const user = this.sessionService.user;
    let fullName = user.name || '';

    if (user.lastname1) {
      fullName += ' ' + user.lastname1;
    }

    return fullName.trim();
  }

  async ngOnInit(): Promise<void> {
    // Verificar permisos del Dashboard
    const dashboardPermissions = new PermissionsConfig(Modules.Dashboard);
    this.hasRoleDashboardView = dashboardPermissions.HasRolePermission(
      this.sessionService,
      Permissions.List
    );

    // Verificar permisos de consulta en Envases
    const packagingPermissions = new PermissionsConfig(Modules.Packagings);
    this.hasRolePackagingsList = packagingPermissions.HasRolePermission(
      this.sessionService,
      Permissions.List
    );

    // Si no tiene permiso, no cargar nada más
    if (!this.hasRoleDashboardView) {
      return;
    }

    // Cargar centros del usuario
    await this.loadCenters();

    // Inicializar campos de filtro
    this.initializeFilterFields();

    // Recuperar estado de filtros guardados si existen
    const savedState = this.searchState.getSearchState('IndexFilters');
    if (savedState) {
      // Aplicar filtros guardados
      if (savedState.idCenter) {
        this.filterForm.patchValue({ idCenter: savedState.idCenter }, { emitEvent: false });
        await this.loadAreas();
      }
      if (savedState.idArea) {
        this.filterForm.patchValue({ idArea: savedState.idArea }, { emitEvent: false });
      }
    }

    // Cargar estadísticas con los filtros aplicados
    await this.loadStatistics();

    // Suscribirse a cambios en el filtro de centro para cargar áreas
    this.filterForm.controls.idCenter.valueChanges.subscribe(async (idCenter) => {
      this.filterForm.controls.idArea.setValue(null, { emitEvent: false });
      if (idCenter) {
        await this.loadAreas();
      } else {
        this.areasList = [];
        // Actualizar opciones en filterFields cuando se limpian las áreas
        const areaField = this.filterFields.find(f => f.name === 'idArea');
        if (areaField) {
          areaField.options = [];
        }
      }
      // Guardar estado de búsqueda (construir manualmente porque setValue no actualiza el value inmediatamente)
      this.searchState.saveSearchState({ idCenter: idCenter, idArea: null }, 'IndexFilters');
      // Pasar explícitamente los valores para evitar race conditions
      await this.loadStatistics(idCenter, null);
    });

    // Suscribirse a cambios en el filtro de área
    this.filterForm.controls.idArea.valueChanges.subscribe(async (idArea) => {
      const idCenter = this.filterForm.controls.idCenter.value;
      // Guardar estado de búsqueda (construir manualmente para asegurar valores actualizados)
      this.searchState.saveSearchState({ idCenter: idCenter, idArea: idArea }, 'IndexFilters');
      // Pasar explícitamente los valores para evitar race conditions
      await this.loadStatistics(idCenter, idArea);
    });
  }

  /**
   * Inicializar campos de filtro
   */
  private initializeFilterFields(): void {
    this.filterFields = [
      {
        name: 'idCenter',
        label: '$packaging.lblCenter',
        type: 'select',
        placeholder: '$general.txtSelect',
        options: this.centersList,
        bindLabel: 'text',
        bindValue: 'value',
        clearable: true,
        searchable: true,
        colSize: 'col-md-4'
      },
      {
        name: 'idArea',
        label: '$packaging.lblArea',
        type: 'select',
        placeholder: '$general.txtSelect',
        options: this.areasList,
        bindLabel: 'text',
        bindValue: 'value',
        clearable: true,
        searchable: true,
        disabled: () => !this.filterForm.value.idCenter,
        colSize: 'col-md-4'
      }
    ];
  }

  /**
   * Cargar centros donde el usuario tiene permisos
   */
  private async loadCenters(): Promise<void> {
    try {
      const idUser = this.sessionService.user.idUser;

      const queryCenters = `
        x.idLocation > 0
        AND x.state = 1
        AND x.type = ${LocationsTypes.Center}
        AND EXISTS (
            SELECT 1
            FROM "UM_UsersLocations" u
            JOIN "LoM_Locations" loc ON loc.idLocation = u.idLocation
            LEFT JOIN "LoM_Locations" client ON client.idLocation = x.idLocationFather
            LEFT JOIN "LoM_Locations" company ON company.idLocation = client.idLocationFather
            WHERE u.idUser = ${idUser}
              AND (
                  -- Usuario en grupo (acceso a todos los centros)
                  (loc.type = ${LocationsTypes.Group} AND company.idLocationFather = loc.idLocation)
                  -- Usuario en empresa (acceso a todos los centros de esa empresa)
                  OR (loc.type = ${LocationsTypes.Company} AND client.idLocationFather = loc.idLocation)
                  -- Usuario en cliente (acceso a todos los centros de ese cliente)
                  OR (loc.type = ${LocationsTypes.Client} AND x.idLocationFather = loc.idLocation)
                  -- Usuario en centro (acceso a ese centro)
                  OR (loc.type = ${LocationsTypes.Center} AND x.idLocation = loc.idLocation)
                  -- Usuario en área (acceso al centro padre)
                  OR (loc.type = ${LocationsTypes.Area} AND loc.idLocationFather = x.idLocation)
              )
        )
      `.trim();

      const selectListParamsCenters: SelectListParams = {
        entity: 'LoM_Locations',
        alias: 'x',
        fieldId: 'idLocation',
        fieldName: 'name',
        query: queryCenters
      };

      this.centersList = await this.selectListsApiService.requestSelectList(selectListParamsCenters);

      // Actualizar opciones en filterFields
      const centerField = this.filterFields.find(f => f.name === 'idCenter');
      if (centerField) {
        centerField.options = this.centersList;
      }
    } catch (error) {
      console.error('Error loading centers:', error);
    }
  }

  /**
   * Cargar áreas del centro seleccionado
   */
  private async loadAreas(): Promise<void> {
    try {
      const idCenter = this.filterForm.value.idCenter;
      if (!idCenter) {
        this.areasList = [];
        return;
      }

      const idUser = this.sessionService.user.idUser;

      const queryAreas = `
        x.idLocation > 0
        AND x.state = 1
        AND x.type = ${LocationsTypes.Area}
        AND x.idLocationFather = ${idCenter}
        AND EXISTS (
            SELECT 1
            FROM "UM_UsersLocations" u
            JOIN "LoM_Locations" loc ON loc.idLocation = u.idLocation
            LEFT JOIN "LoM_Locations" center ON center.idLocation = x.idLocationFather
            LEFT JOIN "LoM_Locations" client ON client.idLocation = center.idLocationFather
            LEFT JOIN "LoM_Locations" company ON company.idLocation = client.idLocationFather
            WHERE u.idUser = ${idUser}
              AND (
                  -- Usuario en grupo (acceso a todas las áreas)
                  (loc.type = ${LocationsTypes.Group} AND company.idLocationFather = loc.idLocation)
                  -- Usuario en empresa (acceso a todas las áreas de esa empresa)
                  OR (loc.type = ${LocationsTypes.Company} AND client.idLocationFather = loc.idLocation)
                  -- Usuario en cliente (acceso a todas las áreas de ese cliente)
                  OR (loc.type = ${LocationsTypes.Client} AND center.idLocationFather = loc.idLocation)
                  -- Usuario en centro (acceso a todas las áreas de ese centro)
                  OR (loc.type = ${LocationsTypes.Center} AND x.idLocationFather = loc.idLocation)
                  -- Usuario en área específica
                  OR (loc.type = ${LocationsTypes.Area} AND x.idLocation = loc.idLocation)
              )
        )
      `.trim();

      const selectListParamsAreas: SelectListParams = {
        entity: 'LoM_Locations',
        alias: 'x',
        fieldId: 'idLocation',
        fieldName: 'name',
        query: queryAreas
      };

      this.areasList = await this.selectListsApiService.requestSelectList(selectListParamsAreas);

      // Actualizar opciones en filterFields
      const areaField = this.filterFields.find(f => f.name === 'idArea');
      if (areaField) {
        areaField.options = this.areasList;
      }
    } catch (error) {
      console.error('Error loading areas:', error);
      this.areasList = [];
    }
  }

  /**
   * Cargar estadísticas de envases por estado
   * Usa el nuevo endpoint optimizado que retorna todos los counts en una sola consulta
   * @param centerParam - ID del centro (opcional, si no se proporciona se toma del formulario)
   * @param areaParam - ID del área (opcional, si no se proporciona se toma del formulario)
   */
  private async loadStatistics(centerParam?: number | null, areaParam?: number | null): Promise<void> {
    this.loading = true;
    try {
      // Usar los parámetros si se proporcionan, sino tomar del formulario
      const idCenter = centerParam !== undefined
        ? (centerParam ? Number(centerParam) : undefined)
        : (this.filterForm.value.idCenter ? Number(this.filterForm.value.idCenter) : undefined);

      const idArea = areaParam !== undefined
        ? (areaParam ? Number(areaParam) : undefined)
        : (this.filterForm.value.idArea ? Number(this.filterForm.value.idArea) : undefined);

      // Parámetros de fechas para el estado "Recibido"
      const dateFrom = this.getLast30DaysDate();
      const dateTo = new Date();

      // Llamar al nuevo endpoint optimizado que retorna todos los counts
      const response = await this.packagingApi.getPackagingStatistics(
        idCenter,
        idArea,
        dateFrom,
        dateTo
      ).toPromise();
      console.log(response);
      // Crear un mapa de counts por estado para acceso rápido
      const countsMap = new Map<number, number>();
      if (response && response.results) {
        response.results.forEach(item => {
          countsMap.set(item.status, item.count);
        });
      }

      // Definir los estados que queremos mostrar en orden
      const statuses = [
        PackagingStatusHelper.InUse,
        PackagingStatusHelper.PendingPickup,
        PackagingStatusHelper.PickedUp,
        PackagingStatusHelper.Transport,
        PackagingStatusHelper.DeliveredToPlant,
        PackagingStatusHelper.Received
      ];

      // Crear el array de statusCounts con los counts del servidor
      this.statusCounts = statuses.map(status => ({
        status: status,
        count: countsMap.get(status) || 0,
        color: PackagingStatusHelper.GetStatusColor(status),
        translateKey: PackagingStatusHelper.GetStatusTranslationKey(status)
      }));

    } catch (error) {
      console.error('Error loading statistics:', error);
      // En caso de error, inicializar con counts en 0
      this.statusCounts = [
        PackagingStatusHelper.InUse,
        PackagingStatusHelper.PendingPickup,
        PackagingStatusHelper.PickedUp,
        PackagingStatusHelper.Transport,
        PackagingStatusHelper.DeliveredToPlant,
        PackagingStatusHelper.Received
      ].map(status => ({
        status: status,
        count: 0,
        color: PackagingStatusHelper.GetStatusColor(status),
        translateKey: PackagingStatusHelper.GetStatusTranslationKey(status)
      }));
    } finally {
      this.loading = false;
    }
  }

  /**
   * Obtener fecha de hace 1 mes (mismo día del mes anterior)
   */
  private getLast30DaysDate(): Date {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  /**
   * Convertir el enum PackagingDateTypes a string para el API
   * @param dateType El valor del enum PackagingDateTypes
   * @returns 'discharge' para Created (1) o 'status' para Status (2)
   */
  private convertDateTypeToString(dateType: PackagingDateTypes): 'discharge' | 'status' {
    return dateType === PackagingDateTypes.Created ? 'discharge' : 'status';
  }


  /**
   * Navegar al listado de envases filtrado por estado
   */
  navigateToPackagingList(status: number): void {
    // Verificar que el usuario tenga permiso de consulta en envases
    const packagingPermissions = new PermissionsConfig(Modules.Packagings);
    if (!packagingPermissions.HasRolePermission(this.sessionService, Permissions.List)) {
      return;
    }

    const queryParams: any = {
      status: status
    };

    // Agregar filtros de centro y área si están seleccionados y tienen valor
    const idCenter = this.filterForm.value.idCenter;
    const idArea = this.filterForm.value.idArea;

    if (idCenter && idCenter !== null && idCenter !== 'null') {
      queryParams.idCenter = idCenter;
    }
    if (idArea && idArea !== null && idArea !== 'null') {
      queryParams.idArea = idArea;
    }

    // Si es el estado "Recibido", agregar filtro de fechas (último mes)
    if (status === PackagingStatusHelper.Received) {
      // Usar el número del enum para queryParams (packaging-list lo convertirá a string)
      queryParams.dateType = PackagingDateTypes.Status;
      queryParams.dateFrom = this.getLast30DaysDate().toISOString();
      queryParams.dateTo = new Date().toISOString();
    }

    this.router.navigate(['/packagings/list'], { queryParams });
  }

  /**
   * Limpiar filtros
   */
  clearFilters(): void {
    // Limpiar estado guardado en localStorage
    this.searchState.clearSearchState('IndexFilters');
    // Resetear formulario
    this.filterForm.reset({ idCenter: null, idArea: null }, { emitEvent: false });
    this.areasList = [];
    // Recargar estadísticas sin filtros
    this.loadStatistics();
  }
}
